import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';
import { environment } from 'src/environments/environment';

/**
 *  FRISSÍTÉS — adminisztrátori képernyő.
 *
 *  Négy kérdésre válaszol, ebben a sorrendben: mi fut most, van-e újabb, rendben
 *  vannak-e az adatbázis-migrációk, és mi történik épp, ha fut egy frissítés.
 *
 *  A frissítés leállással jár, ezért NEM indul magától: az időzítés az intézményé.
 */
@Component({
  selector: 'app-admin-frissites',
  templateUrl: './frissites.component.html',
  styleUrls: ['./frissites.component.scss'],
})
export class AdminFrissitesComponent implements OnInit, OnDestroy {
  intezmeny = '';
  /** Az aktuális modul: a modul adminja is frissíthet (backend/frissites.js). */
  mode = '';
  toltodik = true;
  hiba = '';
  allapot: any = null;

  indit = false;
  /** A választott cél verzió. Alapból a legújabb, de átállítható. */
  celVerzio = '';
  futas: any = null;
  lepesek: any[] = [];
  private idozito: any = null;

  /*  A FRISSÍTŐ SZOLGÁLTATÁS ÖNMAGÁRÓL – VERZIÓSTUL.
   *
   *  MIÉRT LÁTSZIK KÜLÖN? Mert a frissítő képfájlját rögzített címkével hivatkozzuk (ő
   *  cseréli a konténereket, magát nem cserélheti közben), ezért csöndben ottragad egy
   *  régi változaton. Élesben ez odáig jutott, hogy minden frissítés „megszakadt"-tal
   *  zárult egy olyan hiba miatt, amit rég kijavítottunk – csak a javítás nem ért el
   *  hozzá, és sehol nem látszott, melyik változat fut. */
  /*  A BÖNGÉSZŐBEN FUTÓ FELÜLET VERZIÓJA.
   *
   *  A „most fut" szám a KISZOLGÁLÓTÓL jön. A böngészőben lévő felület viszont lehet
   *  régebbi: az alkalmazás egyoldalas, tehát egy nyitva maradt fül a frissítés után is
   *  a RÉGI kódot futtatja tovább – a szám stimmel, az új menüpont mégsem látszik.
   *  Élesben pontosan ez történt, és senki nem tudta mire vélni. */
  get feluletVerzio(): string {
    const v = String((environment as any).felulet_verzio || '').trim();
    return v && !v.startsWith('__') && v !== 'ismeretlen' ? v : '';
  }

  get feluletElavult(): boolean {
    const kiszolgalo = String(this.allapot?.jelenlegi?.verzio || '').trim();
    return !!(this.feluletVerzio && kiszolgalo && this.feluletVerzio !== kiszolgalo);
  }

  /** Teljes újratöltés – a gyorsítótárat is megkerülve. */
  feluletetUjratolt() {
    try { location.reload(); } catch (e) { /* a felhasználó úgyis tud frissíteni */ }
  }

  frissito: any = null;

  // Beállítások
  beallitasNyitva = false;
  beallitas: any = { registry: 'registry.cronom.it', projekt: 'ces', kepek: 'backend,nyilvantarto', ellenorzes_be: true, utemezett_be: true };
  mentesFut = false;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const si = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = String(si?.intezmenyId || '').replace(/^\/+|\/+$/g, '');
    this.mode = String((await this.data.getFromStorage('currentMode')) || '');
    await this.betolt();
  }

  ngOnDestroy() { if (this.idozito) clearInterval(this.idozito); }

  private get p(): string {
    return `?intezmeny=${encodeURIComponent(this.intezmeny)}${this.mode ? `&mode=${encodeURIComponent(this.mode)}` : ''}`;
  }

  async betolt(friss = false) {
    this.toltodik = true;
    this.hiba = '';
    try {
      this.allapot = await firstValueFrom(this.http.get(
        `/api/frissites/allapot${this.p}${friss ? '&friss=1' : ''}`));
      // A cél alapból a legújabb; ha a felhasználó már választott, azt nem írjuk felül.
      if (!this.celVerzio) this.celVerzio = this.allapot?.verzio?.legujabb || '';
      // Ha épp fut egy frissítés, azonnal a lépéseket mutatjuk.
      const fut = (this.allapot?.futasok || []).find((f: any) => f.allapot === 'fut');
      if (fut) this.futastKovet(fut.id);
      //  Külön kérés, és NEM várjuk meg: hálózaton megy a frissítőhöz, ami épp
      //  újraindulhat – a képernyő többi része ne álljon meg emiatt.
      this.frissitoBetolt();
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A frissítés-állapot nem tölthető be.';
    } finally {
      this.toltodik = false;
    }
  }

  private async frissitoBetolt(friss = false) {
    try {
      this.frissito = await firstValueFrom(this.http.get(
        `/api/frissites/frissito${this.p}${friss ? '&friss=1' : ''}`));
    } catch (e) {
      this.frissito = null;
    }
  }

  /** Kézi újrakérdezés – frissítés után az első kérés még a régi konténert érheti. */
  async frissitoUjra() {
    //  Az „Újra" gomb nem a képernyőt tölti újra, hanem a frissítővel FUTTATJA ÚJRA az
    //  önellenőrzést – különben ugyanazt az induláskori eredményt kapnánk vissza.
    this.frissito = null;
    await this.frissitoBetolt(true);
  }

  async beallitasBetoltese() {
    this.beallitasNyitva = !this.beallitasNyitva;
    if (!this.beallitasNyitva) return;
    try {
      this.beallitas = await firstValueFrom(this.http.get(`/api/frissites/beallitas${this.p}`));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A beállítások nem tölthetők be.', 'error');
    }
  }

  async beallitasMentes() {
    this.mentesFut = true;
    try {
      await firstValueFrom(this.http.put(`/api/frissites/beallitas`,
        { ...this.beallitas, intezmeny: this.intezmeny, mode: this.mode }));
      this.data.sendToast('Beállítások mentve.', 'success');
      await this.betolt(true);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A mentés nem sikerült.', 'error');
    } finally {
      this.mentesFut = false;
    }
  }

  /* ── Frissítés ─────────────────────────────────────────────────────────── */

  /*  FRISSÍTHETŐ-E MOST?
   *
   *  Szándékosan NEM a „van újabb verzió" a feltétel. Ha a futó verzió ismeretlen
   *  (a telepítés nem adta át a címkét), nincs mihez hasonlítani – de ettől még
   *  lehet, hogy frissíteni KELL. Korábban ilyenkor a gomb sem jelent meg: a
   *  frissítő telepítve volt, minden készen állt, és mégsem lehetett elindítani.
   *  Az összehasonlítás hiánya csak azt jelenti, hogy nem ÁLLÍTUNK újdonságot –
   *  nem azt, hogy megtiltjuk a frissítést. */
  get indithato(): boolean {
    return !!this.celVerzio && !!this.allapot?.beallitva && !this.allapot?.karbantartas?.be;
  }

  get valaszthatoVerziok(): string[] {
    const v = this.allapot?.verzio?.valaszthato || [];
    if (this.celVerzio && !v.includes(this.celVerzio)) return [this.celVerzio, ...v];
    return v;
  }

  async frissitesIndit() {
    const cel = this.celVerzio || this.allapot?.verzio?.legujabb;
    const uzenet = [
      `Frissítés erre a verzióra: ${cel || 'latest'}.`,
      ...(this.allapot?.verzio?.verzio_ismeretlen
        ? ['', 'FIGYELEM: a most futó verzió ismeretlen, ezért nem tudjuk megmondani,',
           'hogy ez ÚJABB-e nála. A telepítés nem adta át a képfájl címkéjét (APP_VERZIO).']
        : []),
      '',
      'A frissítés alatt a felhasználók egy „frissítés folyamatban" oldalt látnak,',
      'ami magától visszaengedi őket, amint kész. Az adatbázis-migrációk automatikusan',
      'lefutnak, és a rendszer ellenőrzi is, hogy rendben vannak-e.',
      '',
      'Ez leállással jár. Folytatod?',
    ].join('\n');
    if (!confirm(uzenet)) return;

    this.indit = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/frissites/indit',
        { intezmeny: this.intezmeny, mode: this.mode, verzio: cel }));
      this.data.sendToast('A frissítés elindult.', 'success');
      this.futastKovet(r.futas);
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A frissítés nem indult el.', 'error');
    } finally {
      this.indit = false;
    }
  }

  /*  A lépéseket másodpercenként kérdezzük. Frissítés közben a kiszolgáló újraindul,
   *  tehát a lekérés időnként el fog szállni — ez VÁRHATÓ, nem hiba: csendben
   *  továbbpróbálkozunk, különben a normális működés látszana meghibásodásnak. */
  futastKovet(id: string) {
    if (this.idozito) clearInterval(this.idozito);
    this.idozito = setInterval(async () => {
      try {
        const r: any = await firstValueFrom(this.http.get(`/api/frissites/futas/${id}${this.p}`));
        this.futas = r.futas;
        this.lepesek = r.lepesek || [];
        if (r.futas?.allapot !== 'fut') {
          clearInterval(this.idozito);
          this.idozito = null;
          await this.betolt();
        }
      } catch (e) { /* épp újraindul – várunk tovább */ }
    }, 2000);
  }

  /*  MEGSZAKÍTÁS.
   *
   *  Nem „állítsd le azonnal", hanem „ne várjunk tovább erre a futásra": a rendszer
   *  visszaenged mindenkit, a futás pedig lezárul. Egy már elindult képfájl-letöltést
   *  vagy konténer-cserét NEM vágunk félbe – félig lehúzott réteg vagy félig kicserélt
   *  konténer rosszabb, mint kivárni a végét –, ezért a frissítő a következő LÉPÉSNÉL
   *  áll meg.
   *
   *  Ez a gomb arra az esetre van, amikor a frissítés valójában lefutott, csak a lezáró
   *  jelentés veszett el. Enélkül fél órát kellene várni az elakadás-őrre. */
  megszakitFut = false;

  async frissitestMegszakit() {
    const uzenet = [
      'Megszakítod a frissítést?',
      '',
      'A rendszer visszaengedi a felhasználókat, és a futás hibásként lezárul.',
      'A már elindult letöltés vagy konténer-csere BEFEJEZŐDIK – azt félbevágni',
      'többet ártana, mint használna.',
      '',
      'Utána nézd meg a futó verziót, és ha kell, indíts új frissítést.',
    ].join('\n');
    if (!confirm(uzenet)) return;

    this.megszakitFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/frissites/megszakit',
        { intezmeny: this.intezmeny, mode: this.mode, futas: this.futas?.id }));
      if (this.idozito) { clearInterval(this.idozito); this.idozito = null; }
      this.data.sendToast(r?.frissito === false
        ? 'Megszakítva. A frissítő nem válaszolt – ha még dolgozik, a megkezdett lépést befejezi.'
        : 'A frissítés megszakítva.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A megszakítás nem sikerült.', 'error');
    } finally {
      this.megszakitFut = false;
    }
  }

  async migracioFuttat() {
    if (!confirm('A hiányzó adatbázis-migrációk lefuttatása. Folytatod?')) return;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/frissites/migracio',
        { intezmeny: this.intezmeny, mode: this.mode }));
      this.data.sendToast(
        r?.hiba ? `Megállt: ${r.hiba.fajl}` : `${r.futtatott?.length || 0} migráció lefuttatva.`,
        r?.hiba ? 'error' : 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A migráció nem futott le.', 'error');
    }
  }

  async karbantartasValt(be: boolean) {
    const kerdes = be
      ? 'Bekapcsolod a karbantartást? A felhasználók nem tudnak dolgozni, amíg ki nem kapcsolod.'
      : 'Kikapcsolod a karbantartást? A felhasználók újra dolgozhatnak.';
    if (!confirm(kerdes)) return;
    try {
      await firstValueFrom(this.http.post('/api/frissites/karbantartas',
        { intezmeny: this.intezmeny, mode: this.mode, be }));
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A kapcsolás nem sikerült.', 'error');
    }
  }

  allapotSzin(a: string): string {
    return ({ ok: 'success', kesz: 'success', fut: 'primary', hiba: 'danger' } as any)[a] || 'medium';
  }
}
